import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { patientsRoutes } from "./features/patients/PatientRoutes";
import { AuthRoutes } from "./features/auth/AuthRoutes";
import { consultationsRoutes } from "./features/consultations/ConsultationsRoutes";
import { adminRoutes } from "./features/admin/AdminRoutes";
import { AccountRoutes } from "./features/account/AccountRoutes";
import AppointmentRoutes from "./features/appointments/AppointmentRoutes";
import { RemindersRoutes } from "./features/reminders/RemindersRoutes";
import { UnauthLayout } from "./layouts/UnauthLayout";
import { RoleGuard } from "./components/RoleGuard";
import { OnboardingGuard } from "./features/onboarding/components/OnboardingGuard";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage }))
);
const UserProfilePage = lazy(() =>
  import("./pages/UserProfilePage").then((m) => ({ default: m.UserProfilePage }))
);
const ChangePasswordPage = lazy(() =>
  import("./pages/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage }))
);
const SearchResultsPage = lazy(() =>
  import("./pages/SearchResultsPage").then((m) => ({ default: m.SearchResultsPage }))
);
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const DebugPage = lazy(() =>
  import("./features/debug/DebugPage").then((m) => ({ default: m.DebugPage }))
);
const SignUpPage = lazy(() =>
  import("./features/auth/pages/SignUpPage").then((m) => ({ default: m.SignUpPage }))
);
const MagicLinkRegisterPage = lazy(() =>
  import("./features/auth/pages/MagicLinkRegisterPage").then((m) => ({
    default: m.MagicLinkRegisterPage,
  }))
);
const AcceptInvitePage = lazy(() =>
  import("./features/auth/pages/AcceptInvitePage").then((m) => ({ default: m.AcceptInvitePage }))
);
const UnsubscribeRemindersPage = lazy(() =>
  import("./features/reminders/pages/UnsubscribeRemindersPage").then((m) => ({
    default: m.UnsubscribeRemindersPage,
  }))
);
const ConfirmAppointmentPage = lazy(() =>
  import("./features/appointments/pages/ConfirmAppointmentPage").then((m) => ({
    default: m.ConfirmAppointmentPage,
  }))
);
const OnboardingWizardPage = lazy(() =>
  import("./features/onboarding/pages/OnboardingWizardPage").then((m) => ({
    default: m.OnboardingWizardPage,
  }))
);

export const appRoutes: RouteObject[] = [
  // Public / unauthenticated
  {
    element: <UnauthLayout />,
    children: [
      {
        // Public: the landing page has to sell the product to someone who is not signed in,
        // so it cannot sit behind AuthGuard. It redirects signed-in users to their patients.
        path: "/",
        element: <LandingPage />,
      },
      {
        path: "/signup",
        element: <SignUpPage />,
      },
      {
        path: "/magic-link/register",
        element: <MagicLinkRegisterPage />,
      },
      {
        path: "/invite/accept",
        element: <AcceptInvitePage />,
      },
      {
        // Followed from an email client with no session, same as /invite/accept above.
        path: "/reminders/unsubscribe",
        element: <UnsubscribeRemindersPage />,
      },
      {
        // Followed from an email client with no session, same as the routes above.
        path: "/appointments/confirm",
        element: <ConfirmAppointmentPage />,
      },
    ],
  },

  AuthRoutes,
  {
    path: "",
    element: <AppLayout />,
    children: [
      {
        // Pathless guard around every authenticated route (including /onboarding itself,
        // which it lets through) so an abandoned wizard is resumed from wherever the user
        // lands next, not only from the post-registration redirect.
        element: <OnboardingGuard />,
        children: [
      { ...AccountRoutes, element: <RoleGuard roles={["Admin"]} /> },
      AppointmentRoutes,
      RemindersRoutes,
      {
        path: "/dashboard",
        element: <DashboardPage />,
      },

      {
        path: "/search",
        element: <SearchResultsPage />,
      },
      {
        path: "/profile",
        element: <UserProfilePage />,
      },
      {
        path: "/onboarding",
        element: <OnboardingWizardPage />,
      },
      {
        path: "/profile/password",
        element: <ChangePasswordPage />,
      },
      {
        path: "/profile/debug",
        element: <DebugPage />,
      },

      {
        path: "/patients",
        children: patientsRoutes,
      },
      {
        path: "/consultations",
        element: <RoleGuard roles={["Admin", "Doctor"]} />,
        children: consultationsRoutes,
      },
      {
        path: "/admin",
        element: <RoleGuard roles={["Admin"]} />,
        children: adminRoutes,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
        ],
      },
    ],
  },
];
